// SPEC-14 · Criar conta. Three fields, "Continuar →", the legal line.
// The screen checks the form and shows what went wrong; the account
// itself is made behind the SignUp seam.
//
// SPEC-31: the new account gets the language chosen on this device, saved
// once its claim is set.

import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";

import { SIGN_UP_TIMEOUT_MS, SignUpError } from "../account/signUp";
import type { SignUp as SignUpCall, SignUpFailure } from "../account/signUp";
import { canSubmit, fieldErrors, normalize } from "../account/signUpForm";
import type { SignUpDraft } from "../account/signUpForm";
import { Field } from "../components/Field";
import type { Copy } from "../i18n/language";
import { useCopy, useLanguage } from "../i18n/LanguageProvider";
import { forgetPlaces } from "../lib/place";
import { Link, paths, useRouter } from "../lib/router";
import { within } from "../lib/within";

type ErrorKey = keyof Copy["signUp"]["errors"];
type FieldMessages = Partial<Record<keyof SignUpDraft, ErrorKey>>;

const button =
  "flex h-[var(--control-h)] w-full items-center justify-center gap-2 rounded-pill text-body font-semibold " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf";

/** What a failure shows: a message under one field, or the banner under the button. */
function messagesFor(failure: SignUpFailure): FieldMessages | "banner" {
  if (failure === "email-in-use") return { email: "emailInUse" };
  if (failure === "email-invalid") return { email: "emailInvalid" };
  return "banner";
}

export function SignUp({
  signUp,
  draft,
  onDraftChange,
}: {
  signUp: SignUpCall;
  /** Held by the app, so a visit to the Termos keeps what was typed. */
  draft: SignUpDraft;
  onDraftChange: (draft: SignUpDraft) => void;
}) {
  const { navigate } = useRouter();
  const { saveToAccount } = useLanguage();
  const [messages, setMessages] = useState<FieldMessages>({});
  const [banner, setBanner] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const text = useCopy().signUp;
  const field = (name: keyof SignUpDraft) => ({
    value: draft[name],
    onChange: (event: ChangeEvent<HTMLInputElement>) => onDraftChange({ ...draft, [name]: event.target.value }),
    error: messages[name] ? text.errors[messages[name]] : null,
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting || !canSubmit(draft)) return;
    const values = normalize(draft);
    const errors = fieldErrors(values);
    setMessages(errors);
    setBanner(false);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      await within(signUp(values), SIGN_UP_TIMEOUT_MS);
      saveToAccount();
      // Replace, so the back gesture never returns to a form already sent.
      forgetPlaces();
      navigate(paths.profileOnboarding, { replace: true });
    } catch (error) {
      let failure: SignUpFailure = "failed";
      if (error instanceof SignUpError) {
        failure = error.failure;
      } else {
        console.error("[sign-up] no account after the wait", { kind: error instanceof Error ? error.name : typeof error });
      }
      const shown = messagesFor(failure);
      if (shown === "banner") setBanner(true);
      else setMessages(shown);
      setSubmitting(false);
    }
  };

  return (
    <main className="screen-enter flex min-h-dvh flex-col bg-cream px-5 pt-5">
      <header className="flex items-center justify-between">
        <Link
          to={paths.welcome}
          className="flex size-[var(--touch-min)] items-center justify-center rounded-pill bg-violet text-title text-cream no-underline hover:bg-violet-ink hover:text-cream"
        >
          <span aria-hidden="true">←</span>
          <span className="sr-only">{text.back}</span>
        </Link>
        <Link to={paths.signIn} className="text-body font-semibold text-violet no-underline">
          {text.signIn}
        </Link>
      </header>

      <h1 className="pt-6 text-display leading-[var(--lh-display)] font-semibold tracking-[var(--ls-display)]">
        {text.title}
      </h1>
      <p className="pt-3 text-body leading-[var(--lh-body)] text-muted">{text.lead}</p>

      <form noValidate onSubmit={submit} className="flex flex-1 flex-col pt-8">
        <div className="flex flex-col gap-5">
          <Field
            label={text.firstNameLabel}
            placeholder={text.firstNamePlaceholder}
            autoComplete="given-name"
            autoCapitalize="words"
            {...field("firstName")}
          />
          <Field
            label={text.emailLabel}
            placeholder={text.emailPlaceholder}
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            {...field("email")}
          >
            {messages.email === "emailInUse" && (
              <Link to={paths.signIn} className="px-5 text-caption font-semibold text-violet">
                {text.signIn}
              </Link>
            )}
          </Field>
          <Field
            label={text.passwordLabel}
            placeholder={text.passwordPlaceholder}
            hint={text.passwordHint}
            type="password"
            autoComplete="new-password"
            {...field("password")}
          />
        </div>

        <div className="mt-auto flex flex-col gap-3 pt-8 pb-6">
          <button
            type="submit"
            disabled={submitting || !canSubmit(draft)}
            className={`${button} bg-dark text-cream hover:bg-dark-hover disabled:bg-line-10 disabled:text-disabled`}
          >
            {submitting ? (
              text.submitting
            ) : (
              <>
                {text.submit}
                <span aria-hidden="true">→</span>
              </>
            )}
          </button>
          {banner && (
            <p role="alert" className="rounded-card-inner bg-orange-tint px-4 py-3 text-center text-body-sm text-orange-ink">
              {text.errors.failed}
            </p>
          )}
          <p className="text-center text-caption leading-[var(--lh-body)] text-muted">
            {text.legal.before}
            <Link to={paths.terms}>{text.legal.terms}</Link>
            {text.legal.between}
            <Link to={paths.privacy}>{text.legal.privacy}</Link>
            {text.legal.after}
          </p>
        </div>
      </form>
    </main>
  );
}
